import downloadBlob from "../../../../helpers/downloadBlob.ts";

export type DeliverResult = "downloaded" | "shared" | "cancelled";

// Thrown when navigator.share() was rejected with NotAllowedError, i.e. the
// user activation of the original click expired while the PDF was generated.
// The caller asks for a fresh click and calls deliverPdf() again.
export class ShareActivationLostError extends Error {}

// Touch devices get the native share sheet, where "save to files" is one
// option among the apps. Desktop browsers just download.
function canShareFile(file: File) {
  return (
    window.matchMedia?.("(pointer: coarse)").matches &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  );
}

// `activationMayHaveExpired`: false on the fresh click that follows a
// ShareActivationLostError. A NotAllowedError then means sharing is refused
// for good (e.g. by a permissions policy), so the file is downloaded instead
// of asking for yet another click.
export async function deliverPdf(
  file: File,
  { activationMayHaveExpired = true } = {},
): Promise<DeliverResult> {
  if (canShareFile(file)) {
    try {
      // No title / text: iOS drops the file when they are set
      await navigator.share({ files: [file] });
      return "shared";
    } catch (error) {
      const name = error instanceof DOMException ? error.name : undefined;
      if (name === "AbortError") return "cancelled";
      if (name === "NotAllowedError" && activationMayHaveExpired) {
        throw new ShareActivationLostError();
      }
      // Refused for good or any other failure: fall back to a plain download
    }
  }

  downloadBlob(file, file.name);
  return "downloaded";
}
